/**
 * 未保存の変更の登録簿（2026-10-05・UX 監査 A-4）。
 *
 * 以前は、設定を入力してから保存せずに別のページへ移る・タブを閉じる・更新すると、入れた内容が**警告なしに消えた**
 * （店舗設定は 1 ページに保存ボタンが 7 つあり、1 つの枠だけ保存して移ると他の枠は消える。未保存の警告はアプリ全体で 0 件だった）。
 * 各設定カードが「今、未保存の変更があるか」をここへ登録し、アプリ全体の見張り（UnsavedChangesGuard）が、離れる前に確認する。
 *
 * - 登録簿は画面の外（モジュール内）に持つ。カードが外れたら（unmount）自分の分を消す
 * - 比較は浅い `!==`（settingsDiff の diffFields と同じ。保存で「変更はありません」と出す判定と一致させる）
 */

/** scope='store' は、ヘッダーで店舗を切り替えると消える入力（店舗ごとの設定カード）。'page' はページを離れると消える入力（会社設定など。店舗を切り替えても残る） */
export type UnsavedScope = 'page' | 'store';

const dirtyMap = new Map<string, { label: string; scope: UnsavedScope }>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

/** snapshot（読み込み / 保存直後の値）と現在値を比べて、違いがあれば true。snapshot が無い（読み込み前・失敗）は false */
export function isDirty<T extends Record<string, unknown>>(snapshot: T | null | undefined, current: T): boolean {
  if (!snapshot) return false;
  return (Object.keys(current) as (keyof T)[]).some((k) => snapshot[k] !== current[k]);
}

/** id の未保存を登録（label は確認に出す名前）。dirty=false なら外す */
export function setUnsaved(id: string, label: string, dirty: boolean, scope: UnsavedScope = 'page'): void {
  const had = dirtyMap.has(id);
  if (dirty) {
    const cur = dirtyMap.get(id);
    if (cur && cur.label === label && cur.scope === scope) return;
    dirtyMap.set(id, { label, scope });
    emit();
  } else if (had) {
    dirtyMap.delete(id);
    emit();
  }
}

/** 未保存がある項目の名前（登録順）。scope を渡すと、その範囲で消える項目だけ（'store' = 店舗を切り替えると消えるもの） */
export function getUnsavedLabels(scope?: UnsavedScope): string[] {
  return Array.from(dirtyMap.values()).filter((v) => !scope || v.scope === scope).map((v) => v.label);
}

/** 全部外す（確認で「破棄して移動」を選んだとき） */
export function clearUnsaved(): void {
  if (dirtyMap.size === 0) return;
  dirtyMap.clear();
  emit();
}

export function subscribeUnsaved(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

type ConfirmText = { title: string; description: string; confirmLabel: string; cancelLabel: string };

function unsavedList(labels: string[]): string[] {
  const shown = labels.slice(0, 6);
  const more = labels.length - shown.length;
  return [...shown.map((l) => `・${l}`), ...(more > 0 ? [`・ほか ${more} 件`] : [])];
}

/** ヘッダーで店舗を切り替えるときの確認の文面（店舗ごとの設定は、切り替えると別の店舗の値に読み込み直されて消える） */
export function unsavedStoreSwitchConfirmOptions(labels: string[], toStoreName?: string): ConfirmText {
  return {
    title: '保存していない変更があります',
    description: [
      `${toStoreName ? `「${toStoreName}」に切り替える` : '店舗を切り替える'}と、次の項目は、まだ保存していない入力が消えます。`,
      '',
      ...unsavedList(labels),
    ].join('\n'),
    confirmLabel: '保存せずに切り替える',
    cancelLabel: 'この店舗のままにする',
  };
}

/** 確認ダイアログの文面 */
export function unsavedLeaveConfirmOptions(labels: string[]): ConfirmText {
  const shown = labels.slice(0, 6);
  const more = labels.length - shown.length;
  return {
    title: '保存していない変更があります',
    description: [
      '次の項目は、まだ保存していません。このまま移動すると、入力した内容は消えます。',
      '',
      ...shown.map((l) => `・${l}`),
      ...(more > 0 ? [`・ほか ${more} 件`] : []),
    ].join('\n'),
    confirmLabel: '保存せずに移動する',
    cancelLabel: 'このページに戻る',
  };
}
